'use client';
// Per-article "seeder" for the QuickConsult bottom bar (2026-07-23 rewrite). This
// component renders NOTHING itself — it just pushes this article's server-fetched pros/
// variants/category into lib/quickConsultStore.tsx, which the ONE persistent
// QuickConsultBar instance (rendered from SiteShell.tsx) reads from. That's what lets a
// visitor click through to a shown pro's page while the bar stays mounted and untouched —
// see quickConsultStore.tsx and QuickConsultBar.tsx for the actual UI/logic.
import { useEffect } from 'react';
import { useQuickConsult } from '@/lib/quickConsultStore';
import type { QuickConsultPro } from '@/lib/quickConsultTypes';
import type { CtaVariant } from '@/lib/ctaVariantsShared';
import type { Questionnaire } from './QuestionnaireWizard';

export type { QuickConsultPro };

export default function QuickConsultClient({ categorySlug, categoryName, categoryNamePlural, articleId, proId, singlePro, pros, variants, bgAvatars, preciseQuestionnaire, skipCityQuestion, cityLast = false, presetCity, barVariant }: {
  cityLast?: boolean;
  categorySlug: string; categoryName: string; categoryNamePlural?: string; articleId?: number;
  // Set when seeded from a specific pro's page (2026-07-29 widget unification) — not
  // used for filtering, only to give the seed its own identity so navigating between
  // two different pros' pages is treated as a fresh context, same as between articles.
  proId?: number;
  // Set ONLY on a pro's own page (2026-08-22 fix) — see quickConsultStore.tsx's doc.
  singlePro?: QuickConsultPro | null;
  pros: QuickConsultPro[]; variants: CtaVariant[]; bgAvatars: string[];
  preciseQuestionnaire: Questionnaire | null; skipCityQuestion: boolean;
  presetCity?: { name: string; lat: number; lng: number } | null;
  barVariant?: 'full' | 'one' | 'row' | 'pill' | 'seg' | 'sheetpill' | 'sheetfooter' | 'madlibs'; // ?layout= preview (lib/topicLayout.ts)
}) {
  const { seed } = useQuickConsult();
  // 2026-07-29: stable per-context identity — see quickConsultStore.tsx's seedKey doc.
  // keyword-lab topic landings get their own identity so plain ↔ topic navigation reseeds the wizard
  const kwv = preciseQuestionnaire?.version?.startsWith('kwlab-') ? `:${preciseQuestionnaire.version}` : '';
  const seedKey = (articleId ? `a:${articleId}` : proId ? `p:${proId}` : `c:${categorySlug}:${presetCity?.name || ''}${kwv}`) + (barVariant ? `:bar-${barVariant}` : '');

  useEffect(() => {
    seed({ articleId, categorySlug, categoryName, categoryNamePlural: categoryNamePlural || categoryName, pros, singlePro: singlePro ?? null, variants, bgAvatars, preciseQuestionnaire, skipCityQuestion, cityLast, presetCity, seedKey, barVariant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, categorySlug, categoryName, categoryNamePlural, pros, singlePro, variants, bgAvatars, preciseQuestionnaire, skipCityQuestion, cityLast, presetCity, barVariant]);

  return null;
}
